import React, { useState } from 'react';
import { Topic } from '../../types/Topic';
import { ModelResult } from '../../types/ApiResponses';
import { Card } from '../../types/Card';
import { apiClient } from '../../services/apiClient';
import { CardPreview } from './CardPreview';
import './ModelEvaluator.css';

interface ModelEvaluatorProps {
  topics: Topic[];
  onSelectCards: (cards: Card[]) => void;
  onClose: () => void;
}

const MODEL_ORDER = ['nova-lite-2', 'haiku-4-5', 'sonnet-4-6'];

export const ModelEvaluator: React.FC<ModelEvaluatorProps> = ({ topics, onSelectCards, onClose }) => {
  const [results, setResults] = useState<Record<string, ModelResult> | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState('');
  const [pinnedCardsByModel, setPinnedCardsByModel] = useState<Record<string, Set<string>>>({});

  const handleRun = async () => {
    setIsRunning(true);
    setError('');
    try {
      const response = await apiClient.evaluateModels(topics);
      setResults(response.results);
      // Initialise empty pin sets
      const pins: Record<string, Set<string>> = {};
      Object.keys(response.results).forEach(k => { pins[k] = new Set(); });
      setPinnedCardsByModel(pins);
    } catch (err: any) {
      setError(err.message || 'Evaluation failed');
    } finally {
      setIsRunning(false);
    }
  };

  const togglePin = (modelKey: string, cardId: string) => {
    setPinnedCardsByModel(prev => {
      const next = { ...prev };
      const set = new Set(next[modelKey]);
      if (set.has(cardId)) { set.delete(cardId); } else { set.add(cardId); }
      next[modelKey] = set;
      return next;
    });
  };

  const handleUseCards = () => {
    if (!results) return;
    // Collect pinned cards first, then fill with unpinned from best-performing model
    const pinned: Card[] = [];
    const unpinned: Card[] = [];

    MODEL_ORDER.forEach(key => {
      const result = results[key];
      if (!result?.cards) return;
      const pins = pinnedCardsByModel[key] || new Set();
      result.cards.forEach(card => {
        if (pins.has(card.id)) {
          pinned.push({ ...card, isPinned: true });
        } else if (key === 'sonnet-4-6') {
          // Use Sonnet as the fallback pool for unpinned
          unpinned.push(card);
        }
      });
    });

    onSelectCards([...pinned, ...unpinned.slice(0, Math.max(0, 10 - pinned.length))]);
    onClose();
  };

  const totalPinned = Object.values(pinnedCardsByModel).reduce((s, set) => s + set.size, 0);

  return (
    <div className="evaluator-overlay" onClick={onClose}>
      <div className="evaluator-modal" onClick={e => e.stopPropagation()}>
        <div className="evaluator-header">
          <div>
            <h2>Model Evaluation</h2>
            <p>{topics.length} topic{topics.length !== 1 ? 's' : ''} — all 3 models run in parallel</p>
          </div>
          <button className="evaluator-close" onClick={onClose}>✕</button>
        </div>

        {!results && (
          <div className="evaluator-start">
            <div className="model-cards-preview">
              {MODEL_ORDER.map(key => (
                <div key={key} className="model-chip">
                  <span className="model-chip-dot" data-model={key} />
                  {key === 'nova-lite-2' ? 'Nova Lite 2' : key === 'haiku-4-5' ? 'Haiku 4.5' : 'Sonnet 4.6'}
                </div>
              ))}
            </div>
            <p className="evaluator-hint">
              Runs all three models simultaneously. Pin cards you like from any model,
              then use your selection as your card set.
            </p>
            {error && <p className="evaluator-error">{error}</p>}
            <button className="evaluator-run-btn" onClick={handleRun} disabled={isRunning}>
              {isRunning ? (
                <><span className="evaluator-spinner" />Running models in parallel…</>
              ) : 'Run evaluation'}
            </button>
          </div>
        )}

        {results && (
          <>
            <div className="evaluator-columns">
              {MODEL_ORDER.map(key => {
                const result = results[key];
                if (!result) return null;
                const pins = pinnedCardsByModel[key] || new Set();

                return (
                  <div key={key} className="evaluator-column">
                    <div className="evaluator-col-header">
                      <h3>{result.modelLabel}</h3>
                      <div className="evaluator-col-meta">
                        <span title="Latency">{(result.latencyMs / 1000).toFixed(1)}s</span>
                        <span title="Estimated cost">${result.estimatedCostUsd.toFixed(4)}</span>
                        <span title="Tokens">{result.inputTokens + result.outputTokens} tok</span>
                      </div>
                    </div>

                    {result.error ? (
                      <p className="evaluator-col-error">{result.error}</p>
                    ) : (
                      <div className="evaluator-col-cards">
                        {(result.cards || []).map(card => (
                          <div
                            key={card.id}
                            className={`evaluator-card-wrap ${pins.has(card.id) ? 'pinned' : ''}`}
                          >
                            <CardPreview
                              card={{ ...card, isPinned: pins.has(card.id) }}
                              onPin={() => togglePin(key, card.id)}
                              onRegenerate={() => {}}
                              onEdit={() => {}}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="evaluator-footer">
              <span className="evaluator-pin-count">
                {totalPinned > 0
                  ? `${totalPinned} card${totalPinned !== 1 ? 's' : ''} pinned`
                  : 'Pin cards from any column to keep them'}
              </span>
              <div className="evaluator-footer-actions">
                <button className="evaluator-rerun-btn" onClick={handleRun} disabled={isRunning}>
                  Re-run
                </button>
                <button className="evaluator-use-btn" onClick={handleUseCards}>
                  Use {totalPinned > 0 ? `${totalPinned} pinned` : 'Sonnet'} cards
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
